// Columns that are permanently hidden in every table: they are never rendered
// and are not offered in the column manager, but remain untouched in the
// backend data.
export const ALWAYS_HIDDEN_COLUMNS = new Set([
  'Debitornr',
  'Belegnr',
  'Einsatzort_Code',
  'Belegart',
  'Tarifvertrag_Code',
  'Zeiterf_Buch_Blattname',
  'Einsatznr',
  'Einsatzzeitnr'
]);

// Applies a saved { order: [field, ...], hidden: [field, ...] } preference to
// an array of column definitions ({ field, title, ... }).
//
// - Columns marked `pinned: true` are excluded from reordering/hiding and are
//   re-inserted at their original index afterwards (used for the fixed
//   "Aktionen" column).
// - Saved fields that no longer exist are dropped; columns not covered by the
//   saved order (e.g. newly added columns) stay visible and are appended in
//   their default order.
// Returns a new array of column definitions in render order.
export function applyColumnSettings(defs, saved) {
  const pinned = [];
  const manageable = [];
  defs.forEach((def, index) => {
    if (ALWAYS_HIDDEN_COLUMNS.has(def.field)) return;
    if (def.pinned) pinned.push({ def, index });
    else manageable.push(def);
  });

  const hidden = new Set((saved && saved.hidden) || []);
  const remaining = {};
  manageable.forEach(def => { remaining[def.field] = def });

  const ordered = [];
  const take = (def) => {
    ordered.push(def);
    delete remaining[def.field];
  };
  (((saved && saved.order) || [])).forEach(field => {
    if (remaining[field] && !hidden.has(field)) take(remaining[field]);
  });
  manageable.forEach(def => {
    if (remaining[def.field] && !hidden.has(def.field)) take(def);
  });

  pinned.forEach(({ def, index }) => {
    ordered.splice(Math.min(index, ordered.length), 0, def);
  });
  return ordered;
}

// Inverse of applyColumnSettings: given all column definitions and a saved
// preference, returns { order, hidden } as arrays of field names describing
// the visible columns in render order and the hidden ones.
export function resolveColumnState(defs, saved) {
  const fields = defs.filter(d => !d.pinned && !ALWAYS_HIDDEN_COLUMNS.has(d.field)).map(d => d.field);
  const fieldSet = new Set(fields);
  const hidden = (((saved && saved.hidden) || [])).filter(f => fieldSet.has(f));
  const hiddenSet = new Set(hidden);

  const order = [];
  (((saved && saved.order) || [])).forEach(field => {
    if (fieldSet.has(field) && !hiddenSet.has(field) && !order.includes(field)) {
      order.push(field);
    }
  });
  fields.forEach(field => {
    if (!hiddenSet.has(field) && !order.includes(field)) order.push(field);
  });
  return { order, hidden };
}

// With `filters: true`, jspreadsheet puts the filter inputs in a second
// thead row (its cells carry class jss_column_filter) but gives EVERY
// thead cell `position: sticky; top: 0`, so the filter row sticks
// directly on top of the title row and the titles seem to disappear
// when scrolling. This measures the title row's height and exposes it
// as --jss-header-h on the container; the scoped CSS in each view uses
// it to offset the filter cells' sticky top.
export function stickFilterRow(container) {
  const rows = container.querySelectorAll('.jss_worksheet > thead > tr');
  for (const row of rows) {
    if (row.querySelector('td.jss_column_filter')) {
      // Tag the filter row (its first cell has no identifying class)
      row.classList.add('jss-filter-row');
    } else {
      // Title row: its height is the sticky offset for the filter row
      container.style.setProperty('--jss-header-h', row.offsetHeight + 'px');
    }
  }
}

const autocompleteFilters = new WeakMap();
let nextFilterListId = 0;

function getFilterField(column, index) {
  return column.field || column.userField || column.settingField || column.name || column.title || String(index);
}

function updateFilterSuggestions(options, datalist, query) {
  const search = query.trim().toLocaleLowerCase();
  const values = options.filter(value => !search || value.toLocaleLowerCase().includes(search)).slice(0, 20);
  datalist.replaceChildren(...values.map(value => {
    const option = document.createElement('option');
    option.value = value;
    return option;
  }));
}

function filterSignature(filters) {
  return JSON.stringify(Object.keys(filters).sort().map(field => [field, filters[field]]));
}

function renderFilteredRows(state, resetScroll, resetSelection) {
  const worksheet = state.worksheet;
  const data = worksheet.getData();
  const filters = state.columns.reduce((active, column, index) => {
    const query = state.filters.get(getFilterField(column, index));
    if (query) active.push({ index, query: query.toLocaleLowerCase() });
    return active;
  }, []);
  const matches = [];
  data.forEach((row, rowIndex) => {
    if (filters.every(({ index, query }) => String(row[index] == null ? '' : row[index]).toLocaleLowerCase().includes(query))) {
      matches.push(rowIndex);
    }
  });

  worksheet.results = matches.length === data.length ? null : matches;
  if (resetSelection) worksheet.resetSelection();
  const matchingRows = worksheet.results === null ? data.map((_, index) => index) : matches;
  const previousFirstRow = worksheet.tbody.firstChild && Number(worksheet.tbody.firstChild.getAttribute('data-y'));
  let firstIndex = resetScroll ? 0 : matchingRows.indexOf(previousFirstRow);
  if (firstIndex < 0) firstIndex = 0;
  const pageSize = worksheet.options.lazyLoading ? 100 : worksheet.options.pagination || matchingRows.length;
  firstIndex = Math.min(firstIndex, Math.max(0, matchingRows.length - pageSize));

  while (worksheet.tbody.firstChild) worksheet.tbody.removeChild(worksheet.tbody.firstChild);
  const matchingSet = worksheet.results === null ? null : new Set(matches);
  worksheet.rows.forEach((row, index) => {
    row.element.style.display = !matchingSet || matchingSet.has(index) ? '' : 'none';
  });
  matchingRows.slice(firstIndex, firstIndex + pageSize).forEach(index => {
    worksheet.tbody.appendChild(worksheet.rows[index].element);
  });
  if (resetScroll) worksheet.content.scrollTop = 0;
  if (typeof worksheet.updateCornerPosition === 'function') worksheet.updateCornerPosition();

  state.statusLabel.textContent = `${matches.length} / ${data.length} Treffer`;
  if (state.onFilterResults) state.onFilterResults(filters.length ? matches : null);
  state.inputs.forEach(({ input, field }) => {
    const query = state.filters.get(field);
    input.style.backgroundColor = query ? '#eff6ff' : '#fff';
    input.style.borderColor = query ? '#2563eb' : '#9ca3af';
    input.title = query ? `Filter: ${query}` : '';
  });
  return matches.length;
}

function showFilterStatus(state) {
  state.statusLabel.textContent = 'Filter wird angewendet…';
  state.status.style.display = 'flex';
  clearTimeout(state.filterTimer);
  clearTimeout(state.refreshTimer);
  clearTimeout(state.statusTimer);
  state.refreshTimer = null;
  state.filterTimer = setTimeout(() => {
    state.filterTimer = null;
    renderFilteredRows(state, true, true);
    state.statusTimer = setTimeout(() => {
      state.status.style.display = 'none';
      state.statusTimer = null;
    }, 1200);
  }, 100);
}

export function installAutocompleteFilters(container, worksheet, columns, options = {}) {
  removeAutocompleteFilters(container);
  const config = typeof options === 'function' ? { onFilterResults: options } : options || {};
  const initialFilters = Object.fromEntries(Object.entries(config.initialFilters || {})
    .map(([field, value]) => [field, String(value).trim()])
    .filter(([, value]) => value));
  const state = {
    worksheet,
    columns,
    onFilterResults: config.onFilterResults,
    onFilterChange: config.onFilterChange,
    serverSide: typeof config.onFilterChange === 'function',
    filters: new Map(Object.entries(initialFilters)),
    committedFilters: filterSignature(initialFilters),
    inputs: [],
    filterTimer: null,
    refreshTimer: null,
    statusTimer: null,
    status: document.createElement('div'),
    statusLabel: document.createElement('span')
  };
  const data = worksheet.getData();
  const filterRow = worksheet.filter;
  const suggestions = columns.map((column, index) => [...new Set(data
    .map(row => String(row[index] == null ? '' : row[index]))
    .filter(Boolean))].sort((a, b) => a.localeCompare(b)));

  columns.forEach((column, index) => {
    const cell = filterRow && filterRow.children[index + 1];
    if (!cell) return;

    const field = getFilterField(column, index);
    const input = document.createElement('input');
    const datalist = document.createElement('datalist');
    const listId = `jss-column-filter-${++nextFilterListId}`;
    input.type = 'text';
    input.autocomplete = 'off';
    input.placeholder = 'Filtern…';
    input.setAttribute('aria-label', `Freitextfilter für ${column.title || 'Spalte'}`);
    input.setAttribute('data-jss-filter-input', '');
    input.setAttribute('list', listId);
    input.value = state.filters.get(field) || '';
    datalist.id = listId;
    Object.assign(input.style, {
      boxSizing: 'border-box',
      width: '100%',
      minWidth: '0',
      padding: '4px 6px',
      border: '1px solid #9ca3af',
      borderRadius: '3px',
      backgroundColor: '#fff',
      color: '#111827',
      fontSize: '12px'
    });

    const onInput = () => {
      const query = input.value.trim();
      if (query) state.filters.set(field, query);
      else state.filters.delete(field);
      updateFilterSuggestions(suggestions[index], datalist, input.value);
      input.style.backgroundColor = query ? '#eff6ff' : '#fff';
      input.style.borderColor = query ? '#2563eb' : '#9ca3af';
      if (!state.serverSide) showFilterStatus(state);
    };
    const onBlur = event => {
      if (!state.serverSide) return;
      const nextTarget = event.relatedTarget;
      if (nextTarget && nextTarget.closest && nextTarget.closest('[data-jss-filter-input]')) return;
      const filters = Object.fromEntries([...state.filters.entries()].filter(([, query]) => query));
      const signature = filterSignature(filters);
      if (signature === state.committedFilters) return;
      state.committedFilters = signature;
      state.statusLabel.textContent = 'Filter wird geladen…';
      state.status.style.display = 'flex';
      clearTimeout(state.statusTimer);
      state.statusTimer = setTimeout(() => {
        state.status.style.display = 'none';
        state.statusTimer = null;
      }, 10000);
      state.onFilterChange(filters);
    };
    const onKeydown = event => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        input.value = '';
        onInput();
        if (state.serverSide) input.blur();
      } else if (event.key === 'Enter' && state.serverSide) {
        event.preventDefault();
        input.blur();
      }
    };
    input.addEventListener('input', onInput);
    input.addEventListener('blur', onBlur);
    input.addEventListener('keydown', onKeydown);

    cell.classList.remove('jss_column_filter');
    cell.replaceChildren(input, datalist);
    const query = state.filters.get(field);
    input.style.backgroundColor = query ? '#eff6ff' : '#fff';
    input.style.borderColor = query ? '#2563eb' : '#9ca3af';
    state.inputs.push({ input, field, onInput, onBlur, onKeydown });
    updateFilterSuggestions(suggestions[index], datalist, '');
  });

  const originalPosition = container.style.position;
  state.changedPosition = window.getComputedStyle(container).position === 'static';
  if (state.changedPosition) container.style.position = 'relative';
  state.originalPosition = originalPosition;
  state.status.setAttribute('role', 'status');
  state.status.setAttribute('aria-live', 'polite');
  Object.assign(state.status.style, {
    display: 'none',
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: '20',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    border: '1px solid #93c5fd',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#1f2937',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
    pointerEvents: 'none',
    fontSize: '12px'
  });
  const spinner = document.createElement('i');
  spinner.className = 'fa fa-spinner fa-spin';
  spinner.setAttribute('aria-hidden', 'true');
  state.status.append(spinner, state.statusLabel);
  container.appendChild(state.status);
  autocompleteFilters.set(container, state);
}

export function applyAutocompleteFilters(container) {
  const state = autocompleteFilters.get(container);
  if (!state || state.serverSide) return;
  const statusVisible = state.status.style.display !== 'none';
  clearTimeout(state.filterTimer);
  clearTimeout(state.refreshTimer);
  clearTimeout(state.statusTimer);
  state.filterTimer = null;
  state.refreshTimer = setTimeout(() => {
    state.refreshTimer = null;
    renderFilteredRows(state, false, false);
    if (statusVisible) {
      state.statusTimer = setTimeout(() => {
        state.status.style.display = 'none';
        state.statusTimer = null;
      }, 1200);
    }
  }, 0);
}

export function removeAutocompleteFilters(container) {
  const state = autocompleteFilters.get(container);
  if (!state) return;
  clearTimeout(state.filterTimer);
  clearTimeout(state.refreshTimer);
  clearTimeout(state.statusTimer);
  state.inputs.forEach(({ input, onInput, onBlur, onKeydown }) => {
    input.removeEventListener('input', onInput);
    input.removeEventListener('blur', onBlur);
    input.removeEventListener('keydown', onKeydown);
  });
  state.status.remove();
  if (state.changedPosition) container.style.position = state.originalPosition;
  autocompleteFilters.delete(container);
}
